import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link, Navigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import axios from "axios";

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";

// Pages
import HomePage from "./pages/HomePage";
import ShopPage from "./pages/ShopPage";
import ProductPage from "./pages/ProductPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import ProfilePage from "./pages/ProfilePage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import AdminBlogPage from "./pages/AdminBlogPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminOrdersPage from "./pages/AdminOrdersPage";
import AdminRevenuePage from "./pages/AdminRevenuePage";
import AdminLowStockPage from "./pages/AdminLowStockPage";
import AdminReviewsPage from "./pages/AdminReviewsPage";
import BulkShippingEditorPage from "./pages/BulkShippingEditorPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ReturnsPage from "./pages/ReturnsPage";
import ShippingPolicyPage from "./pages/ShippingPolicyPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import VINSearchPage from "./pages/VINSearchPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem("auth_token"));

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      localStorage.removeItem("auth_token");
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("auth_token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("auth_token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem("auth_token");
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    loading,
    token,
    login,
    register,
    loginWithGoogle,
    logout,
    setUser,
    setToken,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Cart Context
const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used within CartProvider");
  return context;
};

const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], subtotal: 0, discount: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, token } = useAuth();

  // Load guest cart from localStorage
  const loadGuestCart = useCallback(() => {
    const storedCart = localStorage.getItem("guestCart");
    if (storedCart) {
      try {
        return JSON.parse(storedCart);
      } catch (e) {
        console.error("Failed to parse guest cart:", e);
      }
    }
    return { items: [], subtotal: 0, discount: 0, total: 0 };
  }, []);

  // Save guest cart to localStorage
  const saveGuestCart = useCallback((cartData) => {
    localStorage.setItem("guestCart", JSON.stringify(cartData));
  }, []);

  // Calculate cart totals
  const calculateTotals = (items) => {
    const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const discount = items.reduce((sum, item) => {
      const itemDiscount = item.price * (item.discount_percent || 0) / 100 * item.quantity;
      return sum + itemDiscount;
    }, 0);
    return { subtotal, discount, total: subtotal - discount };
  };

  const fetchCart = useCallback(async () => {
    if (!isAuthenticated) {
      // Load guest cart from localStorage
      const guestCart = loadGuestCart();
      setCart(guestCart);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/cart`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setCart(response.data);
    } catch (error) {
      console.error("Error fetching cart:", error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token, loadGuestCart]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (productId, quantity = 1) => {
    if (!isAuthenticated) {
      // Guest cart - store in localStorage
      try {
        // Fetch product details
        const response = await axios.get(`${API}/products/${productId}`);
        const product = response.data;
        
        const guestCart = loadGuestCart();
        const existingIndex = guestCart.items.findIndex(item => item.product_id === productId);
        
        if (existingIndex >= 0) {
          guestCart.items[existingIndex].quantity += quantity;
        } else {
          guestCart.items.push({
            product_id: product.product_id,
            name: product.name,
            price: product.price,
            quantity: quantity,
            image: product.images?.[0] || "",
            discount_percent: product.discount_percent || 0
          });
        }
        
        const totals = calculateTotals(guestCart.items);
        const updatedCart = { ...guestCart, ...totals };
        saveGuestCart(updatedCart);
        setCart(updatedCart);
        toast.success("Item added to cart");
        return true;
      } catch (error) {
        toast.error("Failed to add item to cart");
        return false;
      }
    }
    
    // Authenticated cart - use API
    try {
      await axios.post(`${API}/cart/add`, 
        { product_id: productId, quantity },
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      await fetchCart();
      toast.success("Item added to cart");
      return true;
    } catch (error) {
      toast.error("Failed to add item to cart");
      return false;
    }
  };

  const updateQuantity = async (productId, quantity) => {
    if (!isAuthenticated) {
      // Guest cart update
      const guestCart = loadGuestCart();
      const itemIndex = guestCart.items.findIndex(item => item.product_id === productId);
      
      if (itemIndex >= 0) {
        if (quantity <= 0) {
          guestCart.items.splice(itemIndex, 1);
        } else {
          guestCart.items[itemIndex].quantity = quantity;
        }
        const totals = calculateTotals(guestCart.items);
        const updatedCart = { ...guestCart, ...totals };
        saveGuestCart(updatedCart);
        setCart(updatedCart);
      }
      return;
    }

    try {
      await axios.put(`${API}/cart/update`,
        { product_id: productId, quantity },
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      await fetchCart();
    } catch (error) {
      toast.error("Failed to update cart");
    }
  };

  const removeFromCart = async (productId) => {
    if (!isAuthenticated) {
      // Guest cart remove
      const guestCart = loadGuestCart();
      guestCart.items = guestCart.items.filter(item => item.product_id !== productId);
      const totals = calculateTotals(guestCart.items);
      const updatedCart = { ...guestCart, ...totals };
      saveGuestCart(updatedCart);
      setCart(updatedCart);
      toast.success("Item removed from cart");
      return;
    }

    try {
      await axios.delete(`${API}/cart/remove/${productId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      await fetchCart();
      toast.success("Item removed from cart");
    } catch (error) {
      toast.error("Failed to remove item");
    }
  };

  const clearCart = async () => {
    if (!isAuthenticated) {
      // Clear guest cart
      localStorage.removeItem("guestCart");
      setCart({ items: [], subtotal: 0, discount: 0, total: 0 });
      return;
    }

    try {
      await axios.delete(`${API}/cart/clear`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      await fetchCart();
    } catch (error) {
      toast.error("Failed to clear cart");
    }
  };

  const value = {
    cart,
    loading,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    refreshCart: fetchCart,
    itemCount: cart.items.reduce((acc, item) => acc + item.quantity, 0)
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return children;
};

// Admin Route
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user || user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Router with Auth Callback Detection
function AppRouter() {
  const location = useLocation();
  
  // Check for session_id in hash (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/shop/:category" element={<ShopPage />} />
          <Route path="/product/:productId" element={<ProductPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/returns" element={<ReturnsPage />} />
          <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/vin-search" element={<VINSearchPage />} />
          <Route path="/admin/blog" element={<AdminRoute><AdminBlogPage /></AdminRoute>} />
          <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
          <Route path="/admin/orders" element={<AdminRoute><AdminOrdersPage /></AdminRoute>} />
          <Route path="/admin/revenue" element={<AdminRoute><AdminRevenuePage /></AdminRoute>} />
          <Route path="/admin/low-stock" element={<AdminRoute><AdminLowStockPage /></AdminRoute>} />
          <Route path="/admin/reviews" element={<AdminRoute><AdminReviewsPage /></AdminRoute>} />
          <Route path="/admin/shipping-editor" element={<AdminRoute><BulkShippingEditorPage /></AdminRoute>} />
          <Route path="/admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
      <WhatsAppButton />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <Toaster 
            position="top-right" 
            richColors 
            closeButton
            toastOptions={{
              style: {
                background: '#18181B',
                border: '1px solid #27272A',
                color: '#FAFAFA'
              }
            }}
          />
          <AppRouter />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL };
